import { useEffect, useMemo, useState } from "react";
import { DatePicker, Form, InputNumber, message, Modal, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import ComprovanteUpload from "@/components/ComprovanteUpload";
import { ComprovantePayload } from "@/utils/comprovante";
import { getPagamento } from "@/services/pagamento";
import { trocarPagamentoBoleto } from "@/services/boleto";
import { BoletoType } from "@/types/BoletoType";

type Props = {
  boleto: BoletoType | null;
  onClose: () => void;
  onDone: () => void;
};

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const TrocaModal: React.FC<Props> = ({ boleto, onClose, onDone }) => {
  const [pagamentos, setPagamentos] = useState<any[]>([]);
  const [pagamentoId, setPagamentoId] = useState<number | undefined>(undefined);
  const [cv, setCv] = useState<number | null>(null);
  const [dataTransacao, setDataTransacao] = useState<Dayjs | null>(null);
  const [comprovante, setComprovante] = useState<ComprovantePayload | null>(null);

  useEffect(() => {
    if (boleto === null) return;
    setPagamentoId(undefined);
    setCv(null);
    setDataTransacao(null);
    setComprovante(null);
    getPagamento()
      .then(({ data }) =>
        setPagamentos(
          (data ?? []).filter((p: any) => p.ativo && p.tipoPagamento !== "Boleto")
        )
      )
      .catch(() => message.error("Erro ao carregar os meios de pagamento."));
  }, [boleto]);

  const pagamentoSelecionado = useMemo(
    () => pagamentos.find((p) => p.id === pagamentoId),
    [pagamentos, pagamentoId]
  );

  const exigeCv = pagamentoSelecionado?.tipoPagamento === "Cartão";
  const exigeDataTransacao =
    pagamentoSelecionado?.tipoPagamento === "Pix" ||
    (pagamentoSelecionado?.nome ?? "").toLowerCase().includes("transfer");

  const confirmar = async () => {
    if (!boleto) return;
    if (!pagamentoId) {
      message.warning("Selecione o meio de pagamento.");
      return;
    }
    if (exigeCv && !cv) {
      message.warning("Informe o CV (código de autorização) do cartão.");
      return;
    }
    if (exigeDataTransacao && !dataTransacao) {
      message.warning("Informe a data da transação.");
      return;
    }

    try {
      await trocarPagamentoBoleto({
        id: boleto.id,
        pagamentoId,
        cv: cv ?? undefined,
        dataTransacao: dataTransacao ? dataTransacao.format("YYYY-MM-DD") : undefined,
        comprovante: exigeDataTransacao ? comprovante : undefined,
      });
      message.success("Boleto trocado com sucesso!");
      onDone();
    } catch (error: any) {
      message.error(error?.response?.data ?? "Erro ao trocar o meio de pagamento.");
    }
  };

  return (
    <Modal
      title={`Trocar Boleto — parcela ${boleto?.parcela ?? ""} — ${formatValor(boleto?.valor)}`}
      open={boleto !== null}
      onOk={confirmar}
      onCancel={onClose}
      okText="Confirmar troca"
      cancelText="Cancelar"
    >
      <p className="mb-4">
        O boleto será cancelado, a parcela do boleto será cancelada e uma nova
        parcela paga será gerada com o meio de pagamento informado.
      </p>
      {exigeDataTransacao && (
        <p className="mb-4">
          A nova parcela vai aparecer em <strong>Financeiro &gt; Controle Pix/Transferência</strong>{" "}
          como <strong>A Conferir</strong>, indicando que veio da troca deste boleto.
        </p>
      )}
      <Form layout="vertical">
        <Form.Item label="Meio de pagamento" required>
          <Select
            placeholder="Selecione"
            value={pagamentoId}
            onChange={(value) => setPagamentoId(value)}
            options={pagamentos.map((p) => ({
              value: p.id,
              label: `${p.nome} (${p.tipoPagamento})`,
            }))}
          />
        </Form.Item>
        {exigeCv && (
          <Form.Item label="CV (código de autorização)" required>
            <InputNumber
              className="w-full"
              min={1}
              value={cv}
              onChange={(value) => setCv(value)}
            />
          </Form.Item>
        )}
        {exigeDataTransacao && (
          <Form.Item label="Data da transação" required>
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              value={dataTransacao}
              onChange={(data) => setDataTransacao(data)}
              maxDate={dayjs()}
            />
          </Form.Item>
        )}
        {exigeDataTransacao && (
          <Form.Item label="Comprovante do PIX/Transferência (opcional)">
            <ComprovanteUpload comprovante={comprovante} onChange={setComprovante} />
            <div className="text-xs opacity-70 mt-1">
              Dá para anexar depois em Financeiro &gt; Controle Pix/Transferência.
            </div>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default TrocaModal;
